// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/mission-launch/MissionReady.jsx

import React, { useState } from 'react';
import CompletionBanner from './shared/CompletionBanner';

const LAUNCH_TRANSITION_MS = 1000;

const defaultReadyProofItems = [
  {
    label: 'Organization Established',
    detail: 'Business context and launch goals are persisted',
  },
  {
    label: 'Framework Scope Recorded',
    detail: 'Customer-selected launch frameworks are saved',
  },
  {
    label: 'Connector State Verified',
    detail: 'Selected-provider state comes from Enterprise authorization',
  },
  {
    label: 'Guided Operations Ready',
    detail: 'Mission Control and Mission Execution are available for review',
  },
];

export default function MissionReady({
  ready = {},
  error = '',
  onLaunch,
  onLaunchStarted,
}) {
  const [launching, setLaunching] = useState(false);
  const proofItems =
    Array.isArray(ready.proof_items) && ready.proof_items.length
      ? ready.proof_items
      : defaultReadyProofItems;

  const handleLaunch = async () => {
    if (launching) return;

    setLaunching(true);

    if (typeof onLaunchStarted === 'function') {
      onLaunchStarted();
    }

    try {
      if (typeof onLaunch === 'function') {
        await onLaunch();
      }
      await new Promise((resolve) =>
        window.setTimeout(resolve, LAUNCH_TRANSITION_MS)
      );
    } catch {
      setLaunching(false);
    }
  };

  return (
    <div
      className={`mission-step-shell mission-ready-page${
        launching ? ' mission-ready-page-launching' : ''
      }`}
    >
      <CompletionBanner
        eyebrow={ready.eyebrow || 'Mission Launch Review Complete'}
        title={ready.title || 'MISSION READY'}
        lead={
          ready.lead ||
          'Your guided Compliance Operations Center is established.'
        }
        description={
          ready.description ||
          'Launch configuration is complete. Review Mission Control for measured posture and Mission Execution for persisted priorities before taking action.'
        }
      >
        {error ? (
          <div className="connector-launch-error" role="alert">
            <strong>Mission Launch could not be completed.</strong>
            <span>{error}</span>
          </div>
        ) : null}

        <div
          className="mission-ready-proof-grid mission-ready-proof-grid-compact"
          aria-label="Mission Launch completion summary"
        >
          {proofItems.map((item, index) => (
            <div
              className="mission-ready-proof mission-ready-proof-compact"
              key={item.label}
              style={{
                '--mission-ready-card-delay': `${index * 170}ms`,
              }}
            >
              <span aria-hidden="true">✓</span>
              <div>
                <strong>{item.label}</strong>
                <p>{item.detail}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mission-ready-final-action">
          <button
            type="button"
            className="mission-primary-button mission-launch-final-button mission-launch-final-button-premium"
            onClick={handleLaunch}
            disabled={launching || Boolean(error)}
            aria-busy={launching}
          >
            {launching ? (
              <>
                <span
                  className="mission-launch-button-spinner"
                  aria-hidden="true"
                />
                Launching OASYS
              </>
            ) : (
              'Launch OASYS'
            )}
          </button>
          <p>
            {launching
              ? 'Launching Compliance Operations Center...'
              : 'Measured operational state remains available in Mission Control.'}
          </p>
        </div>
      </CompletionBanner>
    </div>
  );
}
